import React, { createContext, useState, useEffect, useContext } from 'react';
import ModalTask from 'components/Organisms/Modals/ModalTask/ModalTask';
import iconCortPng from 'assets/images/home.png';
import Step1 from './Step1/Step1';
import Step2 from './Step2/Step2';
import { Button } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Loan, Task, TaskResponse, TaskState } from 'gql/graphql';
import { useGetTaskResponsesByTask } from 'services/task/querys';
import { LoanContext } from '../../Dashboard';
import StateTag from 'components/Atoms/Tags/StateTag/StateTag';
import CircleTag from 'components/Atoms/Tags/CircleTag/CircleTag';
import { formatterCurrency } from '@common/utils/formatters/formatter';
import { findTaskRules } from '../../RulesTaskCards.constanst';
import { ModuleKeys } from 'services/task/modules-keys.constanst';
import { TaskOpenModalContext } from 'scenes/Private/Private';
import { useModalContext } from '../../../../../sentry/contexts/useModalContext';

const DSCRContext = {
  totalRents: 0,
  setTotalRents: () => { },
  totalUnits: 0,
  setTotalUnits: () => { },
  vacantUnits: 0,
  setVacantUnits: () => { },
  occupiedUnits: 0,
  setOccupiedUnits: () => { }
}

export const TaskContext = createContext<Task | typeof DSCRContext>(DSCRContext);

interface DscrQuestionsAgreementsProps {
  task?: Task;
  currentStep: number | any;
  stateTask?: TaskState;
}
const DscrQuestionsAgreements = ({
  // @ts-ignore
  task = {},
  currentStep,
  stateTask

}: DscrQuestionsAgreementsProps) => {
  const loan: Loan | any = useContext(LoanContext);
  const { value: openContext, setHandler }: any = useContext(TaskOpenModalContext);

  const [isModalTaskVisible, setModalTaskVisible] = useState(false);
  const [isReview, setIsReview] = useState(false);
  const [isApproved, setIsApproved] = useState(false);
  const [step, setStep] = useState(currentStep || 1);

  const [totalRents, setTotalRents] = useState(0);
  const [totalUnits, setTotalUnits] = useState<number>(0);
  const [vacantUnits, setVacantUnits] = useState<number>(0);
  const [occupiedUnits, setOccupiedUnits] = useState<number>(0);
  const [responseLeaseAgreements, setResponseLeaseAgreements] = useState<TaskResponse | any>();
  const [responseMarketRent, setResponseMarketRent] = useState<TaskResponse | any>();
  const [dscrValues, setDSCRValues] = useState<any>({
    monthlyRentalIncome: loan.loanData.monthlyRent,
    annualPropertyInsurance: loan.loanData.annualInsurance,
    annualPropertyTaxes: loan.loanData.annualTaxes,
    annualHOAFee: loan.loanData.annualHoa,
    estimatedDSCR: loan.loanData.selectedOption.dscr
  });
  const { data: taskResponses, reexecute: getTaskResponses } = useGetTaskResponsesByTask({
    variables: { findResponsesByTask: { taskId: task._id } },
    pause: true
  });

  useEffect(() => {
    if (openContext === 'dscr' || openContext === 'dscr') {
      setModalTaskVisible(true);
      setHandler();
    } else if (!!openContext)
      setModalTaskVisible(false);
  }, [openContext]);

  useModalContext({ isModalVisible: isModalTaskVisible, modalKey: 'dscr', task, step });

  useEffect(() => {
    getTaskResponses({ requestPolicy: 'network-only' });
  }, [])

  useEffect(() => {
    setOccupiedUnits(totalUnits - vacantUnits);
  }, [totalUnits, vacantUnits])

  useEffect(() => {
    if (taskResponses && taskResponses.length > 0) {
      const responseMarketRent = taskResponses.find((item) => item.key === 'market-rent');
      const responseLeaseAgreements = taskResponses.find((item) => item.key === 'lease-agreements');
      setResponseLeaseAgreements(responseLeaseAgreements);
      setResponseMarketRent(responseMarketRent);
      if (responseMarketRent?.metadata) {

        const { totalUnits, vacantUnits } = responseMarketRent.metadata;

        if (totalUnits !== undefined) setTotalUnits(totalUnits);
        if (vacantUnits !== undefined) setVacantUnits(vacantUnits);
        if (totalUnits !== undefined && vacantUnits !== undefined) {
          setOccupiedUnits(totalUnits - vacantUnits);
        }
      }
    }
    setIsReview(stateTask === TaskState.Review);
    setIsApproved(stateTask === TaskState.Approved);
  }, [taskResponses]);

  useEffect(() => {
    if (task && task.metadata && Object.keys(task.metadata).length > 0) {
      setDSCRValues({
        ...dscrValues,
      });
      setTotalUnits(task?.metadata?.totalUnits || 0);
      setVacantUnits(task?.metadata?.vacantUnits || 0);
      setOccupiedUnits(task?.metadata?.occupiedUnits || 0);
    }
  }, [task])

  const dscrContextValue = {
    totalRents,
    setTotalRents,
    totalUnits,
    setTotalUnits,
    vacantUnits,
    setVacantUnits,
    occupiedUnits,
    setOccupiedUnits,
    dscrValues,
    setDSCRValues
  };

  const onHelpDocs = () => {
    console.log('onHelpDocs');
  };

  const getFileState = (index: number) => {
    if (!responseLeaseAgreements || !responseLeaseAgreements.files) return 'draft';
    const { state, files } = responseLeaseAgreements;
    const validFiles = files.filter((item: any) => item.state !== 'request_error');
    if (state === TaskState.Review || state === TaskState.Approved) {
      return validFiles[index]?.state || 'draft';
    }
    if (state === TaskState.RequestError) {
      const rejectedFiles = files.filter((item: any) => item.state === 'request_error');
      const neededRejected = Math.max(0, occupiedUnits - validFiles.length);
      const currentFiles = [...validFiles, ...rejectedFiles.slice(-neededRejected)];
      return currentFiles[index]?.state || 'draft';
    }
    return files[index]?.state || 'draft';
  };

  if (!task) return null;

  const renderTracker = () => (
    <div style={{ height: '100%' }}>
      <span className='title-summary'>Task summary</span>
      <div className="mt-20">
        <p className="placeholder" style={{ fontWeight: 500 }}>
          Vacant Units{' '}
        </p>

        <div className='mt-10'>
          <span>Total rent</span>
          <span className='mt-5 price bold'>
            {
              formatterCurrency(
                totalRents,
                false
              )
            }
          </span>
        </div>
        <div>
          <p className="placeholder" style={{ marginTop: 40, fontWeight: 500 }}>
            Occupied Units{' '}
          </p>

          {taskResponses && !responseLeaseAgreements?.files && (
            <div className="owners-tags mt-5">
              <span>{0}/{occupiedUnits} leases submitted</span>
            </div>
          )}
          {taskResponses && occupiedUnits > 0 && responseLeaseAgreements?.files && (
            <div className="owners-tags mt-5">
              <span>{responseLeaseAgreements.files.filter((item: any) => item.state !== 'request_error').length}/{occupiedUnits} leases submitted</span>
            </div>
          )}

          <div className="mt-10 circles-tags">
            {occupiedUnits > 0 && Array.from({ length: occupiedUnits }).map((_, index) => (
              <CircleTag
                state={getFileState(index)}
                tagKey={getFileState(index)}
                key={index}
                isIconState={true}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const { taskName } = findTaskRules(ModuleKeys.DSCR);

  const canNavigateToStep = (targetStep: number) => {
    if (targetStep === 1) return true;
    if (targetStep === 2) {
      return responseMarketRent || responseLeaseAgreements;
    }
  }

  return (
    <TaskContext.Provider value={{ ...task, ...dscrContextValue }}>
      <ModalTask
        task={task}
        visible={isModalTaskVisible}
        handlerSetVisibile={setModalTaskVisible}
        disabledDots={false}
        canNavigateToStep={canNavigateToStep}
        renderButtonTrigger={(openModal: any) => (
          <Button
            className={`button-open-modal button-open-modal-${stateTask}`}
            onClick={() => openModal(true)}
          >
            {(stateTask === TaskState.Review || stateTask === TaskState.Approved) ? 'View task' :
              (stateTask === TaskState.Incomplete) ? 'Complete task' :
              (stateTask === TaskState.RequestError) ? 'Resolve issues' :
              'Begin Task'
            }
          </Button>
        )}
        header={{
          title: <span>{taskName.toUpperCase()}</span>,
          actionLeft: (
            isReview ? <StateTag state={TaskState.Review} size="large" /> :
            isApproved ? <StateTag state={TaskState.Approved} isLabelLocked={true} size="large" /> :
            <img src={iconCortPng} alt="icon" className="paperPng icon-task-state" />
          ),
       /*    actionRight: (
            <Button
              className="button button-transparent"
              onClick={() => onHelpDocs()}
            >
              <QuestionCircleOutlined />
              Help docs
            </Button>
          ) */
        }}
        animationDirection="onRight"
        responses={taskResponses}
        stepsTotal={2}
        currentStep={step}
        setStep={setStep}
        tracker={renderTracker()}
      >
        <section className="children DscrQuestionsAgreements">
          {step === 1 && <Step1 closeTask={() => setModalTaskVisible(false)} setStep={setStep} task={task} />}
          {step === 2 && <Step2 task={task} closeTask={() => setModalTaskVisible(false)} />}
        </section>
      </ModalTask>
    </TaskContext.Provider>
  );
};
export default DscrQuestionsAgreements;
